"use client";

import { useEffect, useState } from "react";
import { MAP, IMAGE_PATHS, GENERAL } from "@/constants";
import style from "./style.module.scss";
import MapService from "@/services/map";
import Image from "next/image";
import { ISearchStationItem, IStation } from "@/interfaces/Map";
import SearchStationItemsContainer from "./SearchStationItemsContainer";
import { Nullable } from "@/types";

type Props = {
  onSelectLocation: (location: Nullable<IStation>) => void;
  clear: () => void;
  selectedMarker: Nullable<IStation>;
  setSelectedMarker: (v: Nullable<IStation>) => void;
  search: string;
  setSearch: (v: string) => void;
  clearSelectedStationData?: () => void;
  isFocused: boolean;
  setIsFocused: (isFocused: boolean) => void;
};

const SearchPanel = ({
  onSelectLocation,
  clear,
  selectedMarker,
  setSelectedMarker,
  search,
  setSearch,
  clearSelectedStationData,
  isFocused,
  setIsFocused,
}: Props) => {
  const { getStationWithSearch } = MapService;
  const [searchResult, setSearchResult] = useState<ISearchStationItem[]>([]);
  const [loading, setLoading] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  useEffect(() => {
    if (search === "") {
      setSearchResult([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      onSelectLocation(null);
      setSelectedMarker(null);

      const normalized = search.toLowerCase();

      const result = (await getStationWithSearch(
        normalized
      )) as ISearchStationItem[];

      setSearchResult(result);
      setLoading(false);
    }, 400);

    return () => {
      clearTimeout(timeoutId);
      setLoading(false);
    };
  }, [search]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSearch(newValue);

    if (isFocused && clearSelectedStationData) {
      clearSelectedStationData();
    }
  };

  const handleClear = () => {
    setSearch("");
    setSearchResult([]);
    setSelectedMarker(null);
    clear();
  };

  const handleSelectStation = (station: ISearchStationItem) => {
    const location = {
      id: station.id,
      latitude: station.latitude?.value,
      longitude: station.longitude?.value,
    } as IStation;
    onSelectLocation(location);
  };

  return (
    <div className={style.searchPanel}>
      <div className={style.inputWrapper}>
        <Image
          src={IMAGE_PATHS.ICONS.SEARCH}
          alt="search"
          width={GENERAL.IMAGE_SIZES.MEDIUM}
          height={GENERAL.IMAGE_SIZES.MEDIUM}
        />
        <input
          className={style.searchInput}
          type="text"
          value={search}
          onChange={(e) => handleChange(e)}
          placeholder={MAP.SEARCH}
          onFocus={handleFocus}
        />
        {search && (
          <button className={style.clearButton} onClick={handleClear}>
            <Image
              src={IMAGE_PATHS.ICONS.CLEAR}
              alt="close"
              width={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
              height={GENERAL.IMAGE_SIZES.EXTRA_LARGE}
            />
          </button>
        )}
      </div>
      {search && isFocused && (
        <SearchStationItemsContainer
          stations={searchResult}
          onSelectStation={handleSelectStation}
          loading={loading}
        />
      )}
    </div>
  );
};

export default SearchPanel;
